import {
  ContentType,
  PublicationType,
  WorkflowEventTypes,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { createWorkflowEventWithStatePolling } from '../../api/CreateWorkflowEventWithStatePolling';
import constants from '../../common/constants';
import ModalDialogButtonV3 from '../common/ModalDialogButtonV3';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import Publication from '../../model/Publication';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';

interface Props {
  contentId: string;
  workflowId: number;
  disabled: boolean;
  publicationType?: PublicationType;
  userId?: string;
  afterFinalizedSuccess?: () => Promise<void>;
  publication?: Publication;
}

export const FinalizeButton = (props: Props) => {
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
  const flashbarMessageBroker: FlashbarMessageBroker = FlashbarMessageBroker.create();
  const {
    publicationType,
    contentId,
    workflowId,
    userId,
    disabled,
    afterFinalizedSuccess,
    publication,
  } = props;
  const content = publication?.content;

  const finalize = async () => {
    try {
      if (!content?.contentData?.href || content?.contentData?.href === 'home') {
        flashbarMessageBroker.pushErrorMessage(
          'Pathslugs for contents should not be empty or default value "home". Please enter a customized pathslug from editing page.',
        );
      } else {
        const workingCopyId =
          publicationType === PublicationType.FINALIZING && userId
            ? `finalizing/${userId}`
            : 'original';
        await createWorkflowEventWithStatePolling(
          contentId,
          workflowId,
          WorkflowEventTypes.FINALIZE,
          '',
          { workingCopyId },
        );
        const { inlineComments } = await mplServiceClient.listInlineComments(contentId, workflowId);
        const updateCommentAttributes = inlineComments
          .filter((comment) => comment.finalizingStartOffset || comment.finalizingEndOffset)
          .map((comment) => {
            const { finalizingStartOffset, finalizingEndOffset, finalizingTextData } = comment;
            return {
              commentId: comment.commentId,
              startOffset: finalizingStartOffset,
              endOffset: finalizingEndOffset,
              textData: finalizingTextData,
              finalizingStartOffset: 0,
              finalizingEndOffset: 0,
              finalizingTextData: '',
            };
          });
        if (updateCommentAttributes.length) {
          await mplServiceClient.updateInlineComments(
            contentId,
            workflowId,
            updateCommentAttributes,
          );
        }
        if (afterFinalizedSuccess) {
          await afterFinalizedSuccess();
        }
      }
    } catch (e) {
      throw new Error('Finalize Content Failed');
    }
  };

  return (
    <ModalDialogButtonV3
      variant="primary"
      header={constants.FINALIZE_MODAL_HEADER}
      text={constants.FINALIZE_MODAL_DESCRIPTION}
      onSubmit={finalize}
      submitButtonText={'Finalize'}
      disabled={disabled}
    />
  );
};
